var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Transition } from 'react-transition-group';
import { CardCore, } from '@core-front/card';
import Badge from '@telia-front/badge';
import Button from '@telia-front/button';
import CloseButton from '@telia-front/close-button';
import { getBackgroundColorClass, } from '@telia-front/colors';
import { debounce } from '@telia-front/helper';
import Image from '@telia-front/image';
import { useLabels } from '@telia-front/label-provider';
import Tag from '@telia-front/tag';
import TagGroup from '@telia-front/tag-group';
import Video from '@telia-front/video';
import { navigate } from '@telia-front/window';
import CardContent from '../card-content/card-content';
import CardFlipContent from '../card-flip-content/card-flip-content';
const CardContext = React.createContext({
    padding: 'medium',
    background: 'primary-4',
    setHasFlipContent: () => false,
});
const Card = (props) => {
    const labels = useLabels();
    const { background, badgeTop, children, className, isFixedHeight, fullWidth, image, isClosable, leftBorderColor, leftBorderColorHex, padding = 'medium', tag, type, onClickClose } = props, rest = __rest(props, ["background", "badgeTop", "children", "className", "isFixedHeight", "fullWidth", "image", "isClosable", "leftBorderColor", "leftBorderColorHex", "padding", "tag", "type", "onClickClose"]);
    const [hasFlipContent, setHasFlipContent] = React.useState(false);
    const [isFlipOpen, setIsFlipOpen] = React.useState(false);
    const [contentHeight, setContentHeight] = React.useState(undefined);
    const [flipHeight, setFlipHeight] = React.useState(undefined);
    const [height, setHeight] = React.useState(undefined);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const cardRef = React.useRef(null);
    const contentRef = React.useRef(null);
    const flipRef = React.useRef(null);
    const updateHeight = React.useCallback(() => {
        if (flipRef.current) {
            setFlipHeight(flipRef.current.clientHeight);
        }
        if (contentRef.current) {
            setContentHeight(contentRef.current.clientHeight);
        }
    }, [setFlipHeight, setContentHeight]);
    const debouncedResizeHandler = React.useMemo(() => debounce(updateHeight, 100), [updateHeight]);
    React.useEffect(() => {
        updateHeight();
    }, [updateHeight]);
    React.useEffect(() => {
        window.addEventListener('resize', debouncedResizeHandler);
        return () => {
            window.removeEventListener('resize', debouncedResizeHandler);
        };
    }, [debouncedResizeHandler]);
    const getBackground = () => {
        if (background) {
            switch (background) {
                case 'transparent':
                    return;
                default:
                    return background;
            }
        }
        return 'primary-3';
    };
    const backgroundColor = getBackground();
    const BEM = () => {
        const classArray = [];
        if (type) {
            classArray.push(`card--type-${type}`);
        }
        if (backgroundColor) {
            classArray.push(getBackgroundColorClass(backgroundColor));
        }
        if (leftBorderColor || leftBorderColorHex) {
            classArray.push('card--left-bordered');
        }
        if (leftBorderColor) {
            classArray.push(`card--left-bordered-${leftBorderColor}`);
        }
        if (fullWidth) {
            classArray.push('card--full-width');
        }
        if (hasFlipContent) {
            classArray.push('card--with-flip-content');
            if (isFlipOpen) {
                classArray.push('is-flipped');
            }
            if (isAnimating) {
                classArray.push('is-animating');
            }
            if (isFixedHeight) {
                classArray.push('is-fixed-height');
            }
        }
        if (isClosable) {
            classArray.push('card--is-closable');
        }
        if (className) {
            classArray.push(className);
        }
        return classArray.join(' ');
    };
    const renderRegularContent = (state) => (React.createElement(React.Fragment, null, React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child;
        }
        if (child.type === CardContent) {
            const BEM = (childProps) => {
                const classArray = [`is-${state}`];
                if (childProps.className) {
                    classArray.push(childProps.className);
                }
                return classArray.join(' ');
            };
            return React.cloneElement(child, Object.assign(Object.assign({}, child.props), { className: BEM(child.props), elementRef: (el) => {
                    if (contentRef.current !== el) {
                        contentRef.current = el;
                        updateHeight();
                    }
                } }));
        }
    })));
    const renderFlipContent = (state) => (React.createElement(React.Fragment, null, React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child;
        }
        if (child.type === CardFlipContent) {
            const BEM = (childProps) => {
                const classArray = [`is-${state}`];
                if (childProps.className) {
                    classArray.push(childProps.className);
                }
                return classArray.join(' ');
            };
            return React.cloneElement(child, Object.assign(Object.assign({}, child.props), { className: BEM(child.props), elementRef: (el) => {
                    if (flipRef.current !== el) {
                        flipRef.current = el;
                        updateHeight();
                    }
                } }));
        }
    })));
    const renderAnimatedContent = () => (React.createElement(React.Fragment, null,
        React.createElement(Transition, { in: !isFlipOpen, timeout: { enter: 150, exit: 150 }, onEnter: () => setIsAnimating(true), onEntering: () => {
                window.setTimeout(() => {
                    setHeight(contentHeight);
                });
            }, onExiting: () => setIsAnimating(true), onExited: () => setIsAnimating(false) }, renderRegularContent),
        React.createElement(Transition, { in: isFlipOpen, timeout: { enter: 150, exit: 150 }, onEnter: () => setIsAnimating(true), onEntering: () => {
                window.setTimeout(() => {
                    setHeight(flipHeight);
                });
            }, onExiting: () => setIsAnimating(true), onExited: () => setIsAnimating(false) }, renderFlipContent)));
    const renderContent = () => (React.createElement(React.Fragment, null,
        isClosable && onClickClose && (React.createElement(CloseButton, { className: "card__close", onClick: onClickClose })),
        image && (React.createElement("div", { className: "card__figure" },
            React.createElement(Image, Object.assign({}, image, { className: "card__image" })))),
        children));
    const getHeight = () => {
        if (hasFlipContent && isAnimating && !isFixedHeight) {
            return height ? height : contentHeight;
        }
    };
    const getBadgeTopProps = () => {
        if (typeof badgeTop === 'object') {
            return badgeTop;
        }
    };
    return (React.createElement(CardContext.Provider, { value: {
            padding: padding,
            background: backgroundColor || 'transparent',
            setHasFlipContent,
        } },
        React.createElement(CardCore, Object.assign({}, rest, { className: BEM(), padding: padding, badgeTop: badgeTop, tag: tag, components: {
                Video,
                Image,
                Badge: (props) => (React.createElement(Badge, Object.assign({}, props, getBadgeTopProps()))),
                Tag,
                TagGroup,
            }, navigate: navigate, style: {
                borderLeftColor: leftBorderColorHex || undefined,
                height: getHeight(),
            }, elementRef: (el) => {
                if (cardRef.current !== el) {
                    cardRef.current = el;
                }
            } }),
            hasFlipContent && (React.createElement(Button, { className: "card__flip-toggle", icon: isFlipOpen ? '#close' : '#info-round', text: labels.get(isFlipOpen ? 'card.closeInfo' : 'card.showInfo'), link: true, onClick: () => setIsFlipOpen(!isFlipOpen) })),
            hasFlipContent ? renderAnimatedContent() : renderContent())));
};
export { Card as default, CardContext, };
